import { Link } from "react-router-dom";
import {
  BarChart3, CalendarDays, ClipboardList, ExternalLink, FilePenLine, HeartPulse,
  KeyRound, Mail, MessageSquareHeart, RefreshCw, ShieldCheck, ShieldHalf, Wrench,
} from "lucide-react";
import LegalLayout from "../components/legal/LegalLayout";
import ClauseList from "../components/legal/ClauseList";
import { LegalCallout, LegalContact } from "../components/legal/LegalBlocks";

const SECTIONS = [
  {
    id: "privacy",
    title: "Privacy & Data Handling",
    icon: ShieldCheck,
    body: (
      <>
        <p>
          Campus Connect may process the information needed to deliver institutional services. Depending on the
          service you use, this can include:
        </p>
        <ClauseList items={[
          "Identity and account information",
          "Contact information",
          "Request and form submissions",
          "Documents you voluntarily upload where a service requires them",
          "Service interaction and status records",
          "Technical and security logs",
          "Feedback or grievance information, where submitted",
        ]} />
        <h3 className="lc-sub">Why information is used</h3>
        <ClauseList items={[
          "To provide the services you request",
          "To process requests and maintain records",
          "To communicate status and updates",
          "To improve platform reliability",
          "To support authorized institutional administration",
          "To meet security and audit requirements",
        ]} />
        <p style={{ marginTop: 14 }}>
          Access to and disclosure of information is limited to authorized institutional purposes, to applicable
          service providers where required, and to legal or institutional requirements.
        </p>
      </>
    ),
  },
  {
    id: "access",
    title: "Access & Account",
    icon: KeyRound,
    body: (
      <ClauseList items={[
        "Access to Campus Connect is based on authorization",
        "Different users see different services and tools",
        "Credentials should not be shared with anyone",
        "You are responsible for activity carried out under your authorized account",
        "Administrative access is restricted to authorized personnel",
      ]} />
    ),
  },
  {
    id: "acceptable-use",
    title: "Acceptable Use",
    icon: ShieldHalf,
    body: (
      <>
        <p>Across all Campus Connect services, users are expected to act responsibly. Please do not:</p>
        <ClauseList variant="avoid" items={[
          "Access accounts, data or tools you are not authorized to use",
          "Impersonate others or submit misleading information",
          "Interfere with or attempt to disrupt the platform",
          "Bypass access controls or extract data through unauthorized automation",
          "Use the platform for unlawful, abusive or non-institutional purposes",
        ]} />
        <p style={{ marginTop: 14 }}>
          The full terms of use are set out in the <Link to="/terms">Terms &amp; Conditions</Link>.
        </p>
      </>
    ),
  },
  {
    id: "accuracy",
    title: "Information Accuracy & Submitted Content",
    icon: FilePenLine,
    body: (
      <>
        <p>You are responsible for the accuracy of the information you provide, including:</p>
        <ClauseList items={[
          "Forms and requests",
          "Grievances and feedback",
          "Uploaded files",
          "Booking information",
          "Lost & Found reports and descriptions",
          "Event and society information",
        ]} />
        <p style={{ marginTop: 14 }}>
          Campus Connect may preserve submissions as institutional records where appropriate.
        </p>
      </>
    ),
  },
  {
    id: "grievance-feedback",
    title: "Grievance & Feedback",
    icon: MessageSquareHeart,
    body: (
      <>
        <p>This applies to the Student Grievance Portal and Campus Feedback.</p>
        <ClauseList items={[
          "Submissions should be genuine and respectful",
          "Information may be reviewed by authorized personnel",
          "Confidentiality of submissions should be respected by those handling them",
          "Outcomes depend on the applicable institutional processes",
        ]} />
        <LegalCallout>
          Submissions are handled by authorized institutional personnel in line with institute processes. Please
          do not assume that any particular outcome or level of confidentiality is guaranteed.
        </LegalCallout>
      </>
    ),
  },
  {
    id: "published-information",
    title: "Notices, Calendars & Published Information",
    icon: CalendarDays,
    body: (
      <>
        <p>
          Student Notices, the Event Calendar, the Student Calendar, the Institute Calendar and Public Forms are
          provided for campus information.
        </p>
        <p>
          Where a discrepancy arises, please follow the officially published or most recently updated
          information from the institute.
        </p>
      </>
    ),
  },
  {
    id: "requests",
    title: "Booking & Permission Services",
    icon: ClipboardList,
    body: (
      <>
        <p>
          This applies collectively to request-type services such as Guest Room Booking, Venue Booking, Library
          Night Pass and Society Night Permission.
        </p>
        <ClauseList items={[
          "Requests are subject to applicable institute rules",
          "Submitting a request does not guarantee approval",
          "You must provide accurate information",
          "Approvals and restrictions are handled by authorized personnel",
          "Instructions shown inside a specific service apply when you use that service",
        ]} />
      </>
    ),
  },
  {
    id: "health",
    title: "Health-Related Services",
    icon: HeartPulse,
    body: (
      <>
        <p>
          TIET Health Hub, where enabled, is an institutional digital support and service interface.
        </p>
        <ClauseList items={[
          "Medical and emergency matters should follow official institute healthcare and emergency procedures",
          "Information on the platform should not be treated as independent medical advice, unless a module explicitly provides clinician-approved content",
        ]} />
      </>
    ),
  },
  {
    id: "administrative-tools",
    title: "Society & Administrative Tools",
    icon: Wrench,
    body: (
      <>
        <p>
          This covers Student Societies, the Society Route Manager, Manage Public Forms, Public UI Management,
          Echo Knowledge and System Analytics.
        </p>
        <p>
          Staff and administrative capabilities are restricted to authorized institutional users and must be
          used only for assigned responsibilities.
        </p>
      </>
    ),
  },
  {
    id: "analytics-security",
    title: "Analytics, Logs & Security",
    icon: BarChart3,
    body: (
      <>
        <p>Operational, security and usage records may be processed for:</p>
        <ClauseList items={[
          "Reliability",
          "Security",
          "Troubleshooting",
          "Audit",
          "Service improvement",
          "Authorized institutional analytics",
        ]} />
      </>
    ),
  },
  {
    id: "external",
    title: "External Links & Third-Party Services",
    icon: ExternalLink,
    body: (
      <p>
        Campus Connect may link to or work with external or third-party institutional services. Those services
        are governed by their own terms and privacy practices, and this policy does not extend to them.
      </p>
    ),
  },
  {
    id: "updates",
    title: "Policy Updates",
    icon: RefreshCw,
    body: (
      <p>
        These policies may evolve as Campus Connect gains new services. The date of the latest update is shown at
        the top of this page.
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact & Questions",
    icon: Mail,
    body: (
      <>
        <p>For questions about these policies, please contact:</p>
        <LegalContact />
      </>
    ),
  },
];

export default function PoliciesPage() {
  return (
    <LegalLayout
      current="policies"
      title="Campus Connect Policies"
      subtitle="Platform-wide policies covering privacy, acceptable use, access, communications, data handling and digital campus services."
      sections={SECTIONS}
    />
  );
}
